/**
 * Generates HTML email template for "Booking Secured".
 * Sent to clients when their trip sheet is finalized, with passenger details and price breakdown.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from '../emailLayout';
import { getAircraftPhotos } from '@/features/tails/utils';
import { formatUTCToDateTime } from '@/utils/formatters';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatPrice, trunc2 } from '@/utils';

/**
 * Render a single aircraft card HTML block for a bid option
 */
const buildAircraftCardHtml = (option) => {
  const aircraft = option?.aircraft;
  const operator = option?.operator;

  const imageUrl = getAircraftPhotos(aircraft);

  const modelName = (() => {
    if (!aircraft) return '';
    if (aircraft.aircraft_make_model) {
      const { manufacturer } = aircraft.aircraft_make_model;
      const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
      if (manufacturer && makeModel) {
        const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
        return `${manufacturer} ${modelPart}`.trim();
      }
      return makeModel || '';
    }
    return aircraft.make_model || aircraft.tail_number || '';
  })();

  const operatorName = operator?.name || '';
  const operatorLocation = [operator?.city, operator?.state].filter(Boolean).join(', ');
  const operatorDisplay = operatorLocation ? `${operatorName} • ${operatorLocation}` : operatorName;

  const price = formatPrice(trunc2(option?.total_price || option?.final_price));

  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), color: '#374151', bg: '#e8e9eb', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', color: '#374151', bg: '#e8e9eb', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', color: '#15803D', bg: '#DCFCE7', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.length > 0
    ? amenities.map(a =>
        `<span style="display:inline-block;padding:4px 12px;background:#e8e9eb;color:#374151;font-size:12px;font-weight:500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
      ).join('')
    : '';

  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:280px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;background-color:#FFFFFF;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#171717;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>
              ${operatorDisplay ? `<p style="margin:0 0 12px;font-size:13px;color:#6B7280;">${icon('building', 14, '#6B7280')} ${operatorDisplay}</p>` : ''}

              <!-- Price -->
              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
              </div>

              <!-- Badges -->
              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

              <!-- About -->
              ${aircraft?.description ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                <p style="margin:0;font-size:13px;color:#171717;line-height:1.5;">${aircraft.description}</p>
              </div>` : ''}

              <!-- Amenities -->
              ${amenitiesHtml ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                <div>${amenitiesHtml}</div>
              </div>` : ''}
            </td>
          </tr>
        </table>`;
};

// Pastel avatar colors that rotate per passenger
const AVATAR_COLORS = [
  { bg: '#FDE8D8', color: '#C2410C' }, // peach / orange
  { bg: '#D8E8FD', color: '#1D4ED8' }, // light blue
  { bg: '#DCFCE7', color: '#15803D' }, // light green
  { bg: '#E8D8FD', color: '#7C3AED' }, // lavender
  { bg: '#FDE8E8', color: '#DC2626' }, // light red
  { bg: '#FDF6D8', color: '#A16207' }, // light yellow
];

/**
 * Get initials from a full name (e.g. "Sarah Mitchell" → "SM")
 */
const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0][0] || '').toUpperCase();
};

/**
 * Build the passenger details section
 */
const buildPassengerDetailsHtml = (passengers) => {
  if (!passengers || passengers.length === 0) return '';

  const passengerCells = passengers.map((p, idx) => {
    const name = typeof p === 'string'
      ? p
      : [p.first_name || p.firstName, p.last_name || p.lastName].filter(Boolean).join(' ') || p.fullName || p.name || '';
    const initials = getInitials(name);
    const avatarColor = AVATAR_COLORS[idx % AVATAR_COLORS.length];

    return `<td width="50%" style="padding:0 4px 8px 0;vertical-align:top;">
                      <div style="border-radius:10px;padding:12px 16px;background-color:#ffffff;">
                        <table role="presentation" cellpadding="0" cellspacing="0">
                          <tr>
                            <td width="36" style="padding-right:10px;vertical-align:middle;">
                              <div style="width:32px;height:32px;border-radius:50%;background-color:${avatarColor.bg};text-align:center;line-height:32px;">
                                <span style="font-size:12px;font-weight:700;color:${avatarColor.color};">${initials}</span>
                              </div>
                            </td>
                            <td style="vertical-align:middle;">
                              <span style="font-size:14px;font-weight:500;color:#111827;">${name}</span>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </td>`;
  });

  const rows = [];
  for (let i = 0; i < passengerCells.length; i += 2) {
    const cell1 = passengerCells[i];
    const cell2 = passengerCells[i + 1] || '<td width="50%"></td>';
    rows.push(`<tr>${cell1}${cell2}</tr>`);
  }

  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#f7f7f7;overflow:hidden;">
          <tr>
            <td>
              <h2 style="margin:0;font-size:18px;font-weight:700;color:#696969;padding:10px 0px 12px;">Passenger Details</h2>
              <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
                ${rows.join('')}
              </table>
            </td>
          </tr>
        </table>`;
};

/**
 * Build the price breakdown section for the accepted bid option
 * (Follows the same layout as the Payment Confirmation template)
 */
const buildPriceBreakdownHtml = (option, modelName) => {
  if (!option) return '';

  const flightCost = (parseFloat(option.total_price) || 0) +
    (parseFloat(option.broker_markup) || 0) +
    (parseFloat(option.platform_fee) || 0);
  const federalExciseTax = parseFloat(option.federal_excise_tax) || 0;
  const segmentTax = parseFloat(option.segment_tax) || 0;
  const totalDue = flightCost + federalExciseTax + segmentTax;
  const creditCardFee = totalDue > 0 ? totalDue * 0.04 : 0;

  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;overflow:hidden;">
          <!-- Title outside the card -->
          <tr>
            <td>
              <h2 style="margin:0;font-size:18px;font-weight:700;color:#696969; padding:10px 0px 12px;">Price Breakdown</h2>
            </td>
          </tr>
          <!-- Bordered card containing price table -->
          <tr>
            <td style="padding:0;background-color:#FFFFFF;border:1px solid #EBEBEB;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="overflow:hidden;">
                <!-- Aircraft model header -->
                <tr>
                  <td colspan="2" style="padding:14px 20px 8px;">
                    <span style="font-size:12px;font-weight:600;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;">${modelName}</span>
                  </td>
                </tr>
                <!-- Flight Cost -->
                <tr>
                  <td style="padding:8px 20px;font-size:14px;color:#374151;">Flight Cost</td>
                  <td align="right" style="padding:8px 20px;font-size:14px;font-weight:500;color:#374151;">${formatPrice(flightCost)}</td>
                </tr>
 
                <tr>
                  <td style="padding:8px 20px;font-size:14px;color:#374151;">Federal Excise Tax (7.5%)</td>
                  <td align="right" style="padding:8px 20px;font-size:14px;color:#374151;font-weight:500;">${formatPrice(federalExciseTax)}</td>
                </tr>

                <tr>
                  <td style="padding:8px 20px;font-size:14px;color:#374151;">International or Segment Tax</td>
                  <td align="right" style="padding:8px 20px;font-size:14px;color:#374151;font-weight:500;">${formatPrice(segmentTax)}</td>
                </tr>

                <!-- Divider -->
                <tr>
                  <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
                </tr>
                <!-- Total Due -->
                <tr>
                  <td style="padding:12px 20px;font-size:15px;font-weight:700;color:#111827;">Total Due</td>
                  <td align="right" style="padding:12px 20px;font-size:20px;font-weight:700;color:#111827;">${formatPrice(totalDue)}</td>
                </tr>
        
                <tr>
                  <td style="padding:4px 20px 14px;font-size:14px;color:#374151;">Credit/Debit Card Fee (3%)</td>
                  <td align="right" style="padding:4px 20px 14px;font-size:14px;color:#374151;font-weight:500;">${formatPrice(creditCardFee)}</td>
                </tr>
                
              </table>
            </td>
          </tr>
        </table>`;
};

/**
 * Build the HTML email body for booking secured notification
 * @param {Object} params
 * @param {Object} params.quote - Quote data
 * @param {Array} params.passengers - Passenger array
 * @param {Object} params.acceptedOption - The accepted bid option (with price fields)
 * @param {Array} params.flights - Flight data array
 * @returns {string} HTML email body
 */
export const buildBookingSecuredEmailHtml = ({ quote, passengers, acceptedOption, flights, organizationName, organization, title = 'Booking Secured' }) => {
  const client = quote?.client;
  const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'Valued Client';

  // Find the accepted bid option
  const option = acceptedOption?.bid_options?.find(opt => opt.status === 'ACCEPTED')
    || quote?.bid_items?.flatMap(bi => bi.bid?.bid_options || [])?.find(opt => opt.status === 'ACCEPTED')
    || quote?.bid_items?.flatMap(bi => bi.bid?.bid_options || [])?.[0];

  // Build aircraft card for the accepted option
  const aircraftCardHtml = option ? buildAircraftCardHtml(option) : '';

  // Passenger details
  const passengerDetailsHtml = buildPassengerDetailsHtml(passengers);

  // Aircraft model name for price breakdown header
  const aircraft = option?.aircraft;
  const modelName = (() => {
    if (!aircraft) return '';
    if (aircraft.aircraft_make_model) {
      const { manufacturer } = aircraft.aircraft_make_model;
      const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
      if (manufacturer && makeModel) {
        const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
        return `${manufacturer} ${modelPart}`.trim();
      }
      return makeModel || '';
    }
    return aircraft.make_model || aircraft.tail_number || '';
  })();

  // Price breakdown
  const priceBreakdownHtml = buildPriceBreakdownHtml(option, modelName);

  // Leg details
  const legRows = (flights || []).map((f, i) => `
                <tr>
                  <td style="padding:12px 0px;border-bottom:1px solid #E5E7EB;${i === 0 ? 'border-top:1px solid #E5E7EB;' : ''}">
                    <span style="font-size:14px;font-weight:600;background-color: #e8e9eb; border-radius: 16px; padding:2px 10px;">${icon('flightTakeoff', 14, '#374151')} ${f.departure_airport || '—'}</span>
                    <span style="margin:0 8px;vertical-align:middle;">${icon('arrowRight', 14, '#9CA3AF')}</span>
                    <span style="font-size:14px;font-weight:600;background-color: #e8e9eb; border-radius: 16px; padding:2px 10px;">${icon('flightLand', 14, '#515c6e')} ${f.arrival_airport || '—'}</span>
                  </td>
                  <td style="padding:12px 32px;border-bottom:1px solid #E5E7EB;${i === 0 ? 'border-top:1px solid #E5E7EB;' : ''}text-align:right;">
                    <span style="font-size:13px;color:#6B7280;">${icon('calendar', 13, '#6B7280')} ${formatUTCToDateTime(f.departure_date, getAirportTimezone(f.departure_airport), "MMM D")}</span>
                    <span style="font-size:13px;color:#6B7280;margin-left:12px;">${icon('time', 13, '#6B7280')} ${formatUTCToDateTime(f.departure_date, getAirportTimezone(f.departure_airport), "HH:mm")}</span>
                  </td>
                </tr>`).join('');


  return `
${emailDocOpen({ title })}
${emailHeader({ title, organization })}

        ${spacer()}

        <!-- ===== CLIENT / QUOTE REF row ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Flight Itinerary For</span><br/>
                    <span style="font-size:16px;font-weight:600;color:#111827;">${clientName}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Company</span><br/>
                    <span style="font-size:16px;font-weight:600;color:#111827;">${organizationName || '-'}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== AIRCRAFT CARD ===== -->
        ${aircraftCardHtml}

        ${legRows ? `
        ${spacer()}

        <!-- ===== LEG DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#F7F7F7;overflow:hidden;">
          <tr>
            <td style="padding:20px 0px 12px;">
              <h2 style="margin:0;font-size:18px;font-weight:700;color:#696969;">Leg Details</h2>
            </td>
          </tr>
          <tr>
            <td style="padding:0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                ${legRows}
              </table>
            </td>
          </tr>
        </table>` : ''}

        ${passengerDetailsHtml ? `
        ${spacer()}

        <!-- ===== PASSENGER DETAILS ===== -->
        ${passengerDetailsHtml}` : ''}

        ${priceBreakdownHtml ? `
        ${spacer()}

        <!-- ===== PRICE BREAKDOWN ===== -->
        ${priceBreakdownHtml}` : ''}

        ${spacer()}

${emailFooter({ organization })}
${emailDocClose()}`;
};
